import { Injectable } from '@angular/core';

export interface AvatarConfig {
  name?: string;
  avatarUrl?: string;
  userId?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

@Injectable({
  providedIn: 'root'
})
export class AvatarService {
  /**
   * Generate initials from a name
   * @param name - Full name of the user
   * @returns Initials (max 2 characters)
   */
  getInitials(name: string | undefined | null): string {
    if (!name || name.trim() === '') {
      return 'U';
    }

    const parts = name.trim().split(/\s+/);
    
    if (parts.length === 1) {
      // Single name: take first 2 characters
      return parts[0].substring(0, 2).toUpperCase();
    }
    
    // Multiple names: take first letter of first and last name
    const firstInitial = parts[0].charAt(0);
    const lastInitial = parts[parts.length - 1].charAt(0);
    return (firstInitial + lastInitial).toUpperCase();
  }

  /**
   * Generate a consistent background color based on name or userId
   * @param identifier - Name or userId to generate color from
   * @returns Hex color code
   */
  getBackgroundColor(identifier: string | undefined | null): string {
    if (!identifier) {
      return '#9CA3AF'; // Default gray
    }

    // Predefined color palette for consistency
    const colors = [
      '#EF4444', // Red
      '#F59E0B', // Amber
      '#10B981', // Green
      '#3B82F6', // Blue
      '#8B5CF6', // Violet
      '#EC4899', // Pink
      '#14B8A6', // Teal
      '#F97316', // Orange
      '#6366F1', // Indigo
      '#06B6D4', // Cyan
    ];

    // Generate a consistent index based on the identifier
    let hash = 0;
    for (let i = 0; i < identifier.length; i++) {
      hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }

  /**
   * Get text color (white or black) based on background color for contrast
   * @param backgroundColor - Hex color code
   * @returns 'white' or 'black'
   */
  getTextColor(backgroundColor: string): string {
    // Convert hex to RGB
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return white for dark backgrounds, black for light backgrounds
    return luminance > 0.5 ? '#1F2937' : '#FFFFFF';
  }

  /**
   * Check if an avatar URL is valid and accessible
   * @param url - Avatar URL to validate
   * @returns Promise<boolean>
   */
  async isValidAvatarUrl(url: string | undefined | null): Promise<boolean> {
    if (!url || url.trim() === '') {
      return false;
    }

    // Check if it's a data URL (base64)
    if (url.startsWith('data:image/')) {
      return true;
    }

    // For HTTP URLs, we'll assume they're valid
    // In a real app, you might want to check if the image loads
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return true;
    }

    return false;
  }

  /**
   * Get avatar URL with fallback logic
   * @param avatarUrl - User's avatar URL
   * @param userId - User's ID for generating consistent fallback
   * @returns Avatar URL or null if should use initials
   */
  getAvatarUrl(avatarUrl: string | undefined | null, userId?: string): string | null {
    // If avatar URL is provided and valid, use it
    if (avatarUrl && avatarUrl.trim() !== '') {
      // Filter out pravatar.cc URLs - we want to replace these
      if (avatarUrl.includes('pravatar.cc')) {
        return null;
      }
      return avatarUrl;
    }

    // No valid avatar URL, return null to trigger initials display
    return null;
  }

  /**
   * Get size classes for avatar
   * @param size - Size variant
   * @returns CSS classes for width and height
   */
  getSizeClasses(size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' = 'md'): string {
    const sizeMap = {
      'xs': 'w-6 h-6 text-xs',
      'sm': 'w-8 h-8 text-sm',
      'md': 'w-10 h-10 text-base',
      'lg': 'w-12 h-12 text-lg',
      'xl': 'w-14 h-14 text-xl',
      '2xl': 'w-16 h-16 text-2xl'
    };

    return sizeMap[size];
  }
}

